import { AlertCircle, ArrowLeft, Loader2, Pencil, RotateCw, Send, ShieldCheck } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { useStore } from '@/store';

import { DeliveriesTab } from './components/DeliveriesTab';
import { EndpointActionsMenu, useEndpointActions } from './components/EndpointActions';
import { EndpointHealthTiles } from './components/EndpointHealthTiles';
import {
  EndpointStatusBadge,
  EnvironmentBadge,
  ManagedEndpointBadge,
} from './components/StatusBadges';
import { useWebhookEndpointHealth } from './hooks/useWebhookEndpointHealth';
import { useWebhookEndpoint } from './hooks/useWebhookEndpoints';
import { useWebhooksOrg } from './hooks/useWebhooksOrg';
import {
  developerSettingsPath,
  EMPTY_VALUE,
  environmentLabel,
  eventTypeLabel,
  httpStatusOf,
  isEndpointActive,
  isEndpointManaged,
  MANAGED_ENDPOINT_HINT,
  secretHintLabel,
  WEBHOOK_TEST_EVENT_TYPE,
  webhookErrorMessage,
  webhooksPath,
} from './utils';

function ConfigRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 py-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-all">{children}</dd>
    </div>
  );
}

/**
 * One endpoint: health tiles, configuration, signing secrets (hint only) and
 * custom header names (values are write-only), and its recent deliveries.
 */
export function WebhookEndpointDetailPage() {
  const navigate = useNavigate();
  const { endpointId = '' } = useParams<{ endpointId: string }>();
  const { organizationId, permissions } = useWebhooksOrg();
  const { getProgramsArray, selectedProgramId } = useStore();

  const query = useWebhookEndpoint(organizationId, endpointId, permissions.canGetEndpoint);
  const endpoint = query.data ?? undefined;
  const endpointList = useMemo(() => (endpoint ? [endpoint] : []), [endpoint]);
  const healthIds = useMemo(() => (endpointId ? [endpointId] : []), [endpointId]);
  const health = useWebhookEndpointHealth(organizationId, healthIds, permissions.canReadHealth);

  const listPath = webhooksPath(organizationId);
  const actions = useEndpointActions({
    organizationId,
    permissions,
    onDeleted: () => navigate(listPath, { replace: true }),
  });

  const programNames = useMemo(() => {
    const byId = new Map(getProgramsArray().map(p => [p.id, p.name]));
    return (endpoint?.filter?.program_ids ?? []).map(id => byId.get(id) || id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint?.filter?.program_ids]);

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item to={developerSettingsPath(organizationId, selectedProgramId)}>
          Settings
        </Breadcrumb.Item>
        <Breadcrumb.Item to={listPath}>Webhooks</Breadcrumb.Item>
        <Breadcrumb.Item>{endpoint?.name || 'Endpoint'}</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (query.isLoading) {
    return (
      <>
        {breadcrumb}
        <div className="max-w-6xl mx-auto w-full px-6 pt-8 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    );
  }

  if (query.isError || !endpoint) {
    const notFound = httpStatusOf(query.error) === 404 || (!query.isError && !endpoint);
    return (
      <>
        {breadcrumb}
        <div className="max-w-6xl mx-auto w-full px-6 pt-8">
          <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">
                  {notFound ? 'Endpoint not found' : 'Failed to load the endpoint'}
                </p>
                {!notFound && (
                  <p className="mt-1">
                    {webhookErrorMessage(query.error, 'Please try again later.')}
                  </p>
                )}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="mt-4" asChild>
            <Link to={listPath}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to webhooks
            </Link>
          </Button>
        </div>
      </>
    );
  }

  const active = isEndpointActive(endpoint);
  // Set up by 100ms support: view, deliveries and retry only (the server
  // refuses every other change from the customer).
  const managed = isEndpointManaged(endpoint);
  const testBlockedReason = managed
    ? MANAGED_ENDPOINT_HINT
    : active
      ? ''
      : 'Resume the endpoint to send a test event.';
  const secrets = endpoint.signing_secrets ?? [];
  const headers = endpoint.custom_headers ?? [];

  return (
    <>
      {breadcrumb}
      <div className="h-full overflow-y-auto">
        <div className="max-w-6xl mx-auto w-full px-6 pt-8 pb-10 space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold break-all">
                  {endpoint.name || endpoint.url_host}
                </h1>
                <EndpointStatusBadge status={endpoint.status} />
                <EnvironmentBadge environment={endpoint.environment} />
                {managed && <ManagedEndpointBadge />}
              </div>
              <p className="mt-1 font-mono text-sm text-muted-foreground break-all">
                {endpoint.url_host}
              </p>
              {endpoint.description && (
                <p className="mt-2 text-sm text-muted-foreground">{endpoint.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {permissions.canSendTestEvent && (
                <SimpleTooltipV2 content={testBlockedReason}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => actions.run('test', endpoint)}
                    disabled={!!testBlockedReason || actions.isSendingTest}
                    data-analytics-id="webhooks:endpoint:send-test"
                  >
                    {actions.isSendingTest ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="mr-2 h-4 w-4" />
                    )}
                    Send test event
                  </Button>
                </SimpleTooltipV2>
              )}
              {permissions.canUpdateEndpoint && (
                <SimpleTooltipV2 content={managed ? MANAGED_ENDPOINT_HINT : ''}>
                  <Button
                    size="sm"
                    onClick={() => actions.run('edit', endpoint)}
                    disabled={managed}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                </SimpleTooltipV2>
              )}
              <EndpointActionsMenu
                endpoint={endpoint}
                permissions={permissions}
                onAction={actions.run}
                showViewDeliveries={false}
              />
            </div>
          </div>

          {managed && (
            <div className="flex items-start gap-3 rounded-md border bg-muted/40 px-4 py-3 text-sm">
              <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                <p className="font-medium">This endpoint is managed by 100ms support</p>
                <p className="mt-1 text-muted-foreground">
                  You can view it and its deliveries and retry failed deliveries. To change, pause
                  or delete it, or to rotate its secret, contact 100ms support.
                </p>
              </div>
            </div>
          )}

          {permissions.canReadHealth && (
            <EndpointHealthTiles
              health={health.byId.get(endpointId)}
              isLoading={health.isLoading}
              unavailable={health.isError}
            />
          )}

          <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y">
                  <ConfigRow label="URL">
                    <span className="font-mono text-xs">{endpoint.url || EMPTY_VALUE}</span>
                  </ConfigRow>
                  <ConfigRow label="Environment">
                    {environmentLabel(endpoint.environment)}
                  </ConfigRow>
                  <ConfigRow label="Events">
                    <div className="flex flex-wrap gap-1">
                      {(endpoint.event_types ?? []).map(type => (
                        <Badge key={type} variant="outline" className="font-normal">
                          {eventTypeLabel(type)}
                        </Badge>
                      ))}
                      <Badge variant="secondary" className="font-normal">
                        {eventTypeLabel(WEBHOOK_TEST_EVENT_TYPE)}
                      </Badge>
                    </div>
                  </ConfigRow>
                  <ConfigRow label="Programs">
                    {programNames.length === 0 ? 'All programs' : programNames.join(', ')}
                  </ConfigRow>
                  <ConfigRow label="Timeout">
                    {endpoint.timeout_seconds ? `${endpoint.timeout_seconds} s` : 'Default'}
                  </ConfigRow>
                  <ConfigRow label="referenceId in payload">
                    {endpoint.omit_reference_id ? 'Omitted' : 'Included'}
                  </ConfigRow>
                  {endpoint.paused_at && (
                    <ConfigRow label="Paused">
                      <FormattedDateTime value={endpoint.paused_at} />
                    </ConfigRow>
                  )}
                  <ConfigRow label="Created">
                    <FormattedDateTime value={endpoint.created_at} />
                  </ConfigRow>
                  <ConfigRow label="Updated">
                    <FormattedDateTime value={endpoint.updated_at} />
                  </ConfigRow>
                </dl>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                  <div>
                    <CardTitle className="text-base">Signing secrets</CardTitle>
                    <CardDescription>Only the last characters are shown.</CardDescription>
                  </div>
                  {permissions.canRotateSecret && (
                    <SimpleTooltipV2 content={managed ? MANAGED_ENDPOINT_HINT : ''}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => actions.run('rotate', endpoint)}
                        disabled={managed}
                      >
                        <RotateCw className="mr-2 h-4 w-4" />
                        Rotate
                      </Button>
                    </SimpleTooltipV2>
                  )}
                </CardHeader>
                <CardContent>
                  {secrets.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{EMPTY_VALUE}</p>
                  ) : (
                    <ul className="space-y-2">
                      {secrets.map(s => (
                        <li
                          key={s.id ?? s.hint}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
                        >
                          <code className="font-mono text-sm">{secretHintLabel(s.hint)}</code>
                          <span className="text-xs text-muted-foreground">
                            {s.expires_at ? (
                              <>
                                Expires <FormattedDateTime value={s.expires_at} />
                              </>
                            ) : (
                              <>
                                Created <FormattedDateTime value={s.created_at} />
                              </>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Custom headers</CardTitle>
                  <CardDescription>Values are write-only and never shown.</CardDescription>
                </CardHeader>
                <CardContent>
                  {headers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">None</p>
                  ) : (
                    <ul className="space-y-1">
                      {headers.map(h => (
                        <li
                          key={h.name}
                          className="flex items-center justify-between font-mono text-sm"
                        >
                          <span>{h.name}</span>
                          <span className="text-muted-foreground">••••••••</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {permissions.canListDeliveries && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">Recent deliveries</h2>
              <DeliveriesTab
                organizationId={organizationId}
                permissions={permissions}
                endpoints={endpointList}
                lockedEndpointId={endpointId}
              />
            </section>
          )}
        </div>
      </div>

      {actions.dialogs}
    </>
  );
}
